import { Component, OnInit, Inject, ChangeDetectorRef, ElementRef, inject, ViewChild, signal } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import Map from 'ol/Map';
import { Polygon, LineString } from 'ol/geom';
import { Draw, Modify, Snap } from 'ol/interaction';
import { Circle as Circle, Fill, Stroke, Style, Text } from 'ol/style';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import { getArea, getLength } from 'ol/sphere';
import * as olProj from 'ol/proj';
import { unByKey } from 'ol/Observable';
import { click as clickCondition } from 'ol/events/condition';
import { never as neverCondition } from 'ol/events/condition';
import VectorSource from 'ol/source/Vector';
import { getVectorContext, toContext } from 'ol/render';
import * as olHas from 'ol/has';
import { MapService } from '../../../services/map.service';
import { OlLayerService } from '../../../services/ol/ol-layer.service';
import { OlTooltip } from '../../../models/ol-tooltip';
import { Subscription } from 'rxjs';
import { BrowserSnifferService } from '../../../services/browser-sniffer.service';
import { Renderer2 } from '@angular/core';
import { RendererFactory2 } from '@angular/core';
import { EventsKey } from 'ol/events';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

export enum MeasureState { OFF, SELECTED_LINE, SELECTED_POLYGON, DRAWING_LINE, DRAWING_POLYGON };
export interface ToolTip { addFirstPoint: boolean, addPoint: boolean, finish: boolean, clickToStart: boolean, doubleClickFinish: boolean, modify: boolean };
@Component({
  selector: 'tt-measure-panel',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonToggleModule,
    TranslateModule
  ],
  templateUrl: './measure-panel.component.html',
  styleUrls: ['./measure-panel.component.scss'],
  standalone: true
})
export class MeasurePanelComponent implements OnInit {

  public measureState = MeasureState; // ! necessary to be available in templat
  public state: MeasureState;
  private map: Map;
  private drawLayer: any;//VectorLayer;
  private drawLayerSource: VectorSource;
  private draw: Draw;
  private modify: Modify;
  private snap: Snap;
  private geometryListener: any;//EventListener;
  private drawnFeature: Feature;
  private measureTooltip: OlTooltip;
  private subscriptions: Subscription = new Subscription();
  private drawLayerStyle: any;// StyleLike;

  private overlayCanvas: HTMLCanvasElement;
  private overlayCtx: CanvasRenderingContext2D;
  private maskCanvas: HTMLCanvasElement;
  private maskCtx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private renderer: Renderer2;
  private olViewportElement: HTMLElement;

  private line: LineString;
  private lineFeature: Feature;
  private polygon: Polygon
  private polygonFeature: Feature
  private mapMoveListener: EventsKey[];
  private vectorRenderListener: EventsKey[];
  private mapWidth: number;
  private mapHeight: number;
  private safeTop: number;

  // public output: string;
  public output = signal<string>('0 m');
  
  public measureTooltipElement: HTMLElement;
  public toolTip: ToolTip = {
    addFirstPoint: false,
    addPoint: false,
    finish: false,
    clickToStart: false,
    doubleClickFinish: false,
    modify: false
  };

  public data = inject<any>(MAT_BOTTOM_SHEET_DATA)
  @ViewChild('elevationProfile') elevationProfile?: ElementRef<HTMLElement>;

  constructor(
    public browserSniffer: BrowserSnifferService,
    private mapService: MapService,
    private olLayerService: OlLayerService,
    public bottomSheetRef: MatBottomSheetRef<MeasurePanelComponent>,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private rendererFactory: RendererFactory2,
    //@Inject(MAT_BOTTOM_SHEET_DATA) public data: any
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }
  //move style obj to ? a service ? a model
  close() {
    this.bottomSheetRef.dismiss();
    this.removeInteractions();
  }
  closeTouch() {
    this.bottomSheetRef.dismiss();
    this.removeInteractionsTouch();
  }

  ngOnInit(): void {
    this.state = MeasureState.SELECTED_LINE;
    this.setDrawLayerStyle();
    this.bottomSheetRef.afterDismissed().subscribe(data => {
      if (this.browserSniffer.isTouchDevice) {
        this.removeInteractionsTouch();
      } else {
        this.removeInteractions();
      }
    })
    this.olViewportElement = document.querySelector<HTMLElement>('.ol-viewport')!;
    this.safeTop = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-top')) || 0;
  }

  ngAfterContentInit() {
    /*
    touch
    */
    if (this.browserSniffer.isTouchDevice) {
      this.setToolTip('addFirstPoint');
      this.map = this.mapService.getMap();
      this.drawLayer = this.olLayerService.getLayerById('drawLayer');
      this.drawLayerSource = this.drawLayer.getSource();
      this.drawLayer.setStyle(this.drawLayerStyle);

      if (!document.getElementById('overlayCanvas_1')) {
        this.createOverlayCanvas();
      }
      this.addOverlayCanvasMarker();
    }

    /*
    notouch
    */
    else {
      // this.state = MeasureState.OFF;
      this.map = this.mapService.getMap();
      this.drawLayer = this.olLayerService.getLayerById("drawLayer");
      this.drawLayer.setStyle(this.drawLayerStyle);

      this.measureTooltipElement = this.renderer.createElement('div');
      this.renderer.addClass(this.measureTooltipElement, 'tooltip-measure');
      this.renderer.addClass(this.measureTooltipElement, 'tooltip-draw');
      this.renderer.setProperty(this.measureTooltipElement, 'id', 'measureTooltip');
      this.renderer.appendChild(this.elementRef.nativeElement, this.measureTooltipElement);

      this.measureTooltip = new OlTooltip(this.map, 'measureTooltip');
      this.measureTooltip.setPositioning('bottom-center');
      this.setToolTip('clickToStart');
      this.addInteractions();
      this.output.set((this.state === MeasureState.SELECTED_LINE) ? '0 m' : '0 m²');
    }
  }

  public addFirstPointLineTouch() {
    this.addInteractionsTouch();
    let firstpointCoords = this.map.getView().getCenter();
    let firstpoint = new Feature({ geometry: new Point(firstpointCoords) })
    this.drawLayerSource.addFeature(firstpoint);
    if (this.state === MeasureState.SELECTED_LINE) {
      //line
      this.line = new LineString([[firstpointCoords[0], firstpointCoords[1]], [firstpointCoords[0], firstpointCoords[1]],]);
      this.lineFeature = new Feature({ geometry: this.line });
      this.drawLayerSource.addFeature(this.lineFeature);
      this.output.set("0 m");
      this.setToolTip('addPoint');
    } else {
      //Polygon
      this.output.set("0 m²");
      this.setToolTip('addPoint');
      this.polygon = new Polygon([[[firstpointCoords[0], firstpointCoords[1]], [firstpointCoords[0], firstpointCoords[1]], [firstpointCoords[0], firstpointCoords[1]]]]);
      this.polygonFeature = new Feature({ geometry: this.polygon });
      this.drawLayerSource.addFeature(this.polygonFeature);
    }
  }

  private addInteractionsTouch() {


    let style1 = new Style({
      stroke: new Stroke({
        color: 'rgba(255,255,255,0.0)',
        width: 0,
      }),
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 1.0)',
          width: 1
        })
        ,
        fill: new Fill({
          color: 'rgba(255, 91, 2, 1.0)'
        })
      })
    });
    this.drawLayer.setStyle(style1);

    if (!this.mapMoveListener) {
      this.mapMoveListener = this.map.on(['pointermove', 'moveend'], (event) => {
        if (this.state === MeasureState.SELECTED_LINE) {
          //replace last two flat coords with mapcenter
          this.line.getFlatCoordinates().splice(
            this.line.getFlatCoordinates().length - 2,
            2,
            this.map.getView().getCenter()[0],
            this.map.getView().getCenter()[1]
          );
          //line
          this.line.setCoordinates(this.makePairedCoordinates(this.line.getFlatCoordinates()), 'XY');
          this.output.set(this.formatLength(this.line));
        }
        //MeasureState.SELECTED_POLYGON
        else {
          this.polygon.getFlatCoordinates().splice(
            this.polygon.getFlatCoordinates().length - 4,
            2,
            this.map.getView().getCenter()[0],
            this.map.getView().getCenter()[1]
          );
          this.polygon.setCoordinates([this.makePairedCoordinates(this.polygon.getFlatCoordinates())], 'XY')
          this.output.set(this.formatArea(this.polygon));

        }
        this.map.render();
      });
    }
    let style = new Style({
      stroke: new Stroke({
        color: 'rgba(255, 91, 2, 1.0)',
        width: 5,
        lineDash: [3, 10],
        lineDashOffset: 10,
        lineCap: 'square'
      })
    })

    //immediate rendering on canvas
    if (!this.vectorRenderListener) {
      this.vectorRenderListener = this.drawLayer.on('postrender', (evt) => {
        // evt.context.save();
        let vectorContext = getVectorContext(evt);
        vectorContext.setStyle(style);
        if (this.state === MeasureState.SELECTED_POLYGON && this.polygon !== null) {
          vectorContext.drawGeometry(this.polygon);
        }
        if (this.state === MeasureState.SELECTED_LINE && this.line !== null) {
          vectorContext.drawGeometry(this.line);
        }
      });
    }
  }

  public addPointTouch() {
    if (this.state === MeasureState.SELECTED_POLYGON) {
      this.polygon.getFlatCoordinates().splice(this.polygon.getFlatCoordinates().length - 4, 0, this.map.getView().getCenter()[0], this.map.getView().getCenter()[1]);
    } else {
      this.line.getFlatCoordinates().push(this.map.getView().getCenter()[0], this.map.getView().getCenter()[1]);
    }
  }

  private disableNoMapInteractions() {
    this.renderer.setAttribute(document.querySelector<HTMLElement>('.tt-header'), 'inert', '');
    if (this.elevationProfile) {console.log('check this out!!!'); this.renderer.setAttribute(this.elevationProfile, 'inert', ''); }
    Array.from(document.getElementsByClassName('map-control-button')).forEach(element => {
      this.renderer.setStyle(element, 'pointer-events', 'none');
    });
    if (!this.browserSniffer.isPhone()) {
      if (document.getElementsByTagName('body')[0].classList.contains('trace-drawer-open')) {
        this.renderer.setAttribute(document.querySelector<HTMLElement>('.tt-trace-drawer'), 'inert', '');
      };
      if (document.getElementsByTagName('body')[0].classList.contains('legend-drawer-open')) {
        this.renderer.setAttribute(document.querySelector<HTMLElement>('.tt-legend-drawer'), 'inert', '');
      };
    }
  }
  private enableNoMapInteractions() {
    this.renderer.removeAttribute(document.querySelector<HTMLElement>('.tt-header'), 'inert', '');
    if (this.elevationProfile) { this.renderer.removeAttribute(this.elevationProfile, 'inert', ''); }
    // this.renderer.removeAttribute(document.querySelector<HTMLElement>('.elevationProfile'), 'inert', '');
    Array.from(document.getElementsByClassName('map-control-button')).forEach(element => {
      this.renderer.removeStyle(element, 'pointer-events');
    });
    if (!this.browserSniffer.isPhone()) {
      if (document.getElementsByTagName('body')[0].classList.contains('trace-drawer-open')) {
        this.renderer.removeAttribute(document.querySelector<HTMLElement>('.tt-trace-drawer'), 'inert', '');
      };
      if (document.getElementsByTagName('body')[0].classList.contains('legend-drawer-open')) {
        this.renderer.removeAttribute(document.querySelector<HTMLElement>('.tt-legend-drawer'), 'inert', '');
      };
    }
  }

  public stopMeasuringTouch() {
    this.removeMapListenersTouch();
    this.setToolTip('finish');
    let style = new Style({
      stroke: new Stroke({
        color: 'rgba(255,125,0,1.0)',
        width: 3,
      }),
    });
    if (this.state === MeasureState.SELECTED_POLYGON) {
      this.polygonFeature.setStyle(this.drawLayerStyle);
    } else {
      this.lineFeature.setStyle(this.drawLayerStyle);
    }
    if (this.overlayCanvas) {
      this.overlayCanvas.remove();
      this.maskCanvas.remove();
      this.enableNoMapInteractions();
    };
  }

  private removeInteractionsTouch() {
    if (this.overlayCanvas) {
      this.overlayCanvas.remove();
      this.maskCanvas.remove();
      this.enableNoMapInteractions();
    };
    this.removeMapListenersTouch();
    this.drawLayer.getSource().clear();
    this.subscriptions.unsubscribe();
  }

  private removeMapListenersTouch() {
    unByKey(this.mapMoveListener);
    unByKey(this.vectorRenderListener);
    delete (this.mapMoveListener);
    delete (this.vectorRenderListener);
  }

  public restartMeasuringTouch() {
    this.removeInteractionsTouch();
    this.drawLayer.setStyle(this.drawLayerStyle);
    this.setToolTip('addFirstPoint');
    if (!document.getElementById('overlayCanvas')) {
      this.createOverlayCanvas();
    }
    this.addOverlayCanvasMarker();
  }

  private makePairedCoordinates(coordinates) {
    let item = [];
    let paired = [];
    for (var i = 0, ii = coordinates.length; i < ii; ++i) {
      item.push(coordinates[i])
      if (i % 2) { paired.push(item); item = [] }
    }
    return paired;
  }

  private addInteractions() {
    this.draw = this.createDrawInteraction();
    this.map.addInteraction(this.draw);

    this.modify = this.createModifyInteraction();
    this.map.addInteraction(this.modify);

    this.snap = this.createSnapInteraction();
    this.map.addInteraction(this.snap);

    this.subscriptions.add(
      this.bottomSheetRef.afterDismissed().subscribe(() => {
        this.removeInteractions();
      })
    )
  }

  private removeInteractions() {
    this.map.removeInteraction(this.draw);
    this.map.removeInteraction(this.modify);
    this.map.removeInteraction(this.snap);
    unByKey(this.geometryListener);
    this.drawLayer.getSource().clear();
    this.measureTooltip.deactivate();
    this.subscriptions.unsubscribe();
  }

  public restartDrawing() {
    this.output.set((this.state === MeasureState.SELECTED_LINE) ? '0 m' : '0 m²');
    this.drawnFeature = null;
    this.setToolTip('clickToStart');
    this.refresh();
    this.measureTooltip.deactivate();
    this.drawLayer.getSource().clear();
    unByKey(this.geometryListener);
    this.draw.setActive(true);
  }

  private updateInteractions() {
    this.setToolTip('clickToStart');
    this.output.set((this.state === MeasureState.SELECTED_LINE) ? '0 m' : '0 m²');
    this.removeInteractions();
    this.addInteractions();
  }
  // private updateInteractionsTouch() {
  //   this.removeInteractionsTouch();
  //   this.addInteractionsTouch();
  // }

  private createDrawInteraction() {
    const type = (this.state === MeasureState.SELECTED_POLYGON) ? 'Polygon' : 'LineString';
    const draw = new Draw({
      // active: false,
      source: this.drawLayer.getSource(),
      type: type,
      style: this.getDrawStyle(),
      stopClick: true,
      freehandCondition: neverCondition
    });
    draw.set('active', true);

    draw.on('drawstart', (evt) => {
      // console.log('drawstart')
      this.bottomSheetRef.disableClose = true;
      this.setToolTip('doubleClickFinish');
      this.measureTooltip.setOffset([0, -14]);
      this.measureTooltip.activate();

      this.drawnFeature = evt.feature;
      this.geometryListener = this.drawnFeature.getGeometry().on('change', (evt) => {
        const geom = evt.target;
        let text, position;
        if (geom instanceof Polygon) {
          text = this.formatArea(geom);
          this.output.set(text);
          position = geom.getInteriorPoint().getCoordinates();
        } else if (geom instanceof LineString) {
          text = this.formatLength(geom);
          this.output.set(text);
          this.refresh();
          position = geom.getLastCoordinate();
        }

        if ((type === 'Polygon')) {
          this.measureTooltip.setPosition(position);
          this.measureTooltip.setText(text);
        } else {
          this.measureTooltip.setPosition(position);
          this.measureTooltip.setText(text);
        }
      });
      document.addEventListener("keyup", this.escapeKey);
    });

    draw.on('drawend', (evt) => {
      this.bottomSheetRef.disableClose = false;
      document.removeEventListener("keyup", this.escapeKey);
      this.setToolTip('modify');
      this.refresh();
      //! nodig ?
      //this.drawnFeature = null;
      //this.measureTooltipElement.className = 'tooltip-draw tooltip-measure';
      this.measureTooltip.setOffset([0, -7]);
      //put in timecue to prevent doubleClick map event
      setTimeout(
        () => {
          this.draw.setActive(false);
        }, 0)
    });
    return draw;
  }

  private createModifyInteraction() {
    return new Modify({
      source: this.drawLayer.getSource(),
      deleteCondition: clickCondition
    });
  };

  private createSnapInteraction() {
    return new Snap({
      source: this.drawLayer.getSource()
    });
  };

  private getDrawStyle() {
    return [
      new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)'
        }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 255, 1.0)',
          width: 6,
          lineDash: [3, 10],
          lineDashOffset: 10,
          lineCap: 'square'
        }),
        image: new Circle({
          radius: 6,
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 1.0)',
            width: 2
          }),
          fill: new Fill({
            color: 'rgba(255, 91, 2, 1.0)'
          })
        })
      }),
      new Style({
        stroke: new Stroke({
          color: 'rgba(255, 91, 2, 1.0)',
          width: 4,
          lineDash: [3, 10],
          lineDashOffset: 10,
          lineCap: 'square'

        })
      })
    ]
  }

  private escapeKey = (evt) => {
    if (evt && (evt.keyCode === 27)) {
      this.draw.removeLastPoint();
    }
  }

  private setToolTip(type) {
    Object.keys(this.toolTip).forEach(key => {
      if (type === key) {
        this.toolTip[key] = true;
      } else {
        this.toolTip[key] = false;
      }
    });
    this.refresh();
  }

  private formatLength(line) {
    //lamb08
    let length = getLength(line, { projection: olProj.get('EPSG:3812'), radius: 6378137 });
    let output;
    if (length > 1000) {
      output = (Math.round(length / 1000 * 100) / 100) + ' ' + 'km';
    } else {
      output = (Math.round(length * 100) / 100) + ' ' + 'm';
    }
    this.refresh();
    return output;
  }

  private formatArea(polygon) {
    let area = getArea(polygon, { projection: olProj.get('EPSG:3812'), radius: 6378137 });
    let output;
    if (area > 100000) {
      output = Math.round((area / 1000000) * 100) / 100 + ' ' + 'km²';
    }
    else {
      output = Math.round(area * 100) / 100 + ' ' + 'm²';
    }
    let length = this.formatLength(new LineString(polygon.getLinearRing(0).getCoordinates()));
    output += '<br><span class="perimeter">' + length + '</span>';
    return output;
  }

  public onClickMeasureButton(event: any) {
    this.state = event.value;
    if (this.browserSniffer.isTouchDevice) {
      this.restartMeasuringTouch();
    } else {
      this.updateInteractions();
      //this.restartDrawing();
    }
  }

  private setDrawLayerStyle() {
    this.drawLayerStyle = (feature, resolution) => {
      let geometry: any = feature.getGeometry();
      let styles = [
        new Style({
          fill: new Fill({
            color: 'rgba(255, 91, 2, 0.1)'
          }),
          stroke: new Stroke({
            color: 'rgba(255, 255, 255, 1.0)',
            width: resolution < 5 ? 7 : 5
          })
        }),
        new Style({
          stroke: new Stroke({
            color: 'rgba(255, 91, 2, 1.0)',
            width: resolution < 5 ? 4 : 2
          })
          ,
          zIndex: 99
        })
      ];

      if (geometry.getType() == 'LineString') {
        //firspoint
        styles.push(
          new Style({
            geometry: new Point(geometry.getFirstCoordinate()),
            image: new Circle({
              radius: 6,
              stroke: new Stroke({
                color: 'rgba(255, 255, 255, 1.0)',
                width: 1
              })
              ,
              fill: new Fill({
                color: 'rgba(255, 91, 2, 1.0)'
              })
            }),
            zIndex: 9
          })
        );
        geometry.forEachSegment((start, end) => {
          //add vertex points 
          styles.push(
            new Style({
              geometry: new Point(end),
              image: new Circle({
                radius: 6,
                stroke: new Stroke({
                  color: 'rgba(255, 255, 255, 1.0)',
                  width: 1
                })
                ,
                fill: new Fill({
                  color: 'rgba(255, 91, 2, 1.0)'
                })
              }),
              zIndex: 9
            })
          );
        })
      }

      if (geometry.getType() == 'Polygon') {
        if (resolution < 5) {//2
          geometry.getCoordinates()[0].forEach((coords, index) => {
            if (index > 0) {
              styles.push(
                new Style({
                  geometry: new Point([coords[0], coords[1]]),
                  image: new Circle({
                    radius: 6,
                    stroke: new Stroke({
                      color: 'rgba(255, 255, 255, 1.0)',
                      width: 1
                    })
                    ,
                    fill: new Fill({
                      color: 'rgba(255, 91, 2, 1.0)'
                    })
                  })
                  ,
                  zIndex: 19
                })
              )
            }

          })
        }
      }
      return styles;
    }
  }

  private refresh(): void {
    // without this magic call :-), nothing changes properly
    this.changeDetectorRef.detectChanges();
  }

  private createMaskCanvas() {
    this.maskCanvas = this.renderer.createElement('canvas');
    this.renderer.setProperty(this.maskCanvas, 'id', 'maskCanvas');
    this.renderer.setProperty(this.maskCanvas, 'style', 'position:fixed;top: 0px;z-index:99;pointer-events:none');
    this.renderer.setProperty(this.maskCanvas, 'width', this.mapWidth + 366);
    this.renderer.setProperty(this.maskCanvas, 'height', this.mapHeight + this.safeTop + 250); //250: give a bit space for open elevation profle

    this.olViewportElement.style.overflow = "visible";
    // to avoid layer position problems with fixed elements, append to tt-map  
    this.renderer.appendChild(document.getElementsByTagName('tt-map')[0], this.maskCanvas);
    // like prepend
    // this.renderer.insertBefore(
    //   document.getElementsByClassName('ol-viewport')[0],
    //   this.maskCanvas,
    //   document.getElementsByClassName('ol-layers')[0]
    // );
    this.maskCtx = this.maskCanvas.getContext('2d');
    this.maskCtx.clearRect(0, 0, this.mapWidth, this.mapHeight);
  }

  private createOverlayCanvas() {
    this.mapWidth = this.map.getSize()[0];
    this.mapHeight = this.map.getSize()[1];
    this.canvas = document.getElementsByTagName('canvas')[0];
    this.ctx = document.getElementsByTagName('canvas')[0].getContext('2d');
    //clipping mask
    this.createMaskCanvas();

    this.overlayCanvas = this.renderer.createElement('canvas');
    this.renderer.setProperty(this.overlayCanvas, 'id', 'overlayCanvas_1');
    this.renderer.setProperty(this.overlayCanvas, 'style', 'position:fixed;top:0px;z-index:3;pointer-events:none');
    this.renderer.setProperty(this.overlayCanvas, 'width', this.mapWidth);
    this.renderer.setProperty(this.overlayCanvas, 'height', this.mapHeight);//
    // this.renderer.appendChild(document.getElementsByClassName('ol-viewport')[0], this.overlayCanvas);

    const elm = document.querySelector<HTMLElement>('.ol-viewport')!;
    elm.style.overflow = "visible";

    // to avoid layer position problems with fixed elements, append to tt-map  
    this.renderer.appendChild(document.getElementsByTagName('tt-map')[0], this.overlayCanvas);
    // like prepend
    // this.renderer.insertBefore(
    //   document.getElementsByClassName('ol-viewport')[0],
    //   this.overlayCanvas,
    //   document.getElementsByClassName('ol-layers')[0]
    // );
    this.overlayCtx = this.overlayCanvas.getContext('2d');
    this.overlayCtx.clearRect(0, 0, this.mapWidth, this.mapHeight);


  }

  private addOverlayCanvasMarker() {
    this.disableNoMapInteractions();
    olHas.DEVICE_PIXEL_RATIO;
    let radius = 11;
    let linewidth = 2;
    const viewportTop = this.olViewportElement.getBoundingClientRect().top;
    this.overlayCtx.globalCompositeOperation = "source-over";

    //inner point
    this.overlayCtx.beginPath();
    // this.overlayCtx.lineWidth = 3;
    //circle
    this.overlayCtx.fillStyle = 'rgb(0, 0, 0)';
    this.overlayCtx.arc(this.mapWidth / 2, (this.mapHeight / 2) + viewportTop, 2, 0, 2 * Math.PI);
    this.overlayCtx.fill();
    //white background
    // //outer circle
    this.overlayCtx.lineCap = "square";
    this.overlayCtx.beginPath();
    this.overlayCtx.lineWidth = linewidth + 3;
    this.overlayCtx.strokeStyle = 'rgb(255, 255, 255)';
    this.overlayCtx.arc(this.mapWidth / 2, (this.mapHeight / 2) + viewportTop, radius, 0, 2 * Math.PI);
    this.overlayCtx.stroke();
    //first indent
    this.overlayCtx.beginPath();
    this.overlayCtx.lineWidth = linewidth + 3;
    this.overlayCtx.moveTo((this.mapWidth / 2), (this.mapHeight / 2) - radius * 1.5 + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2), (this.mapHeight / 2) - radius + viewportTop);
    this.overlayCtx.stroke();
    //2nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2) - radius * 1.5, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2) - radius, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.stroke();
    //3nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2) + radius * 1.5, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2) + radius, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.stroke();
    //4nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2), (this.mapHeight / 2) + radius * 1.5 + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2), (this.mapHeight / 2) + radius + viewportTop);
    this.overlayCtx.stroke();

    // this.overlayCtx.shadowColor = 'rgba(255, 255, 255, 1.0)';
    // this.overlayCtx.shadowBlur = 2;
    // this.overlayCtx.shadowOffsetX = 0;
    // this.overlayCtx.shadowOffsetY = 0;

    // this.overlayCtx.lineCap = "butt";

    //outer circle
    this.overlayCtx.beginPath();
    this.overlayCtx.lineWidth = linewidth;
    this.overlayCtx.strokeStyle = 'rgb(0, 0, 0)';
    this.overlayCtx.arc(this.mapWidth / 2, (this.mapHeight / 2) + viewportTop, radius, 0, 2 * Math.PI);
    this.overlayCtx.stroke();
    //first indent
    this.overlayCtx.beginPath();
    this.overlayCtx.lineWidth = linewidth;
    this.overlayCtx.moveTo((this.mapWidth / 2), (this.mapHeight / 2) - radius * 1.5 + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2), (this.mapHeight / 2) - radius + viewportTop);
    this.overlayCtx.stroke();
    //2nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2) - radius * 1.5, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2) - radius, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.stroke();
    //3nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2) + radius * 1.5, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2) + radius, (this.mapHeight / 2) + viewportTop);
    this.overlayCtx.stroke();
    //4nd indent
    this.overlayCtx.beginPath();
    this.overlayCtx.moveTo((this.mapWidth / 2), (this.mapHeight / 2) + radius * 1.5 + viewportTop);
    this.overlayCtx.lineTo((this.mapWidth / 2), (this.mapHeight / 2) + radius + viewportTop);
    this.overlayCtx.stroke();

    this.overlayCtx.save();

    //mask
    this.maskCtx.globalCompositeOperation = "source-out";
    //background    
    this.maskCtx.clearRect(0, 0, this.mapWidth, this.mapHeight);
    this.maskCtx.fillStyle = 'rgba(51, 51, 51, 0.2)';
    this.maskCtx.fillRect(0, 0, this.mapWidth + 366, this.mapHeight + this.safeTop + 250);
    this.maskCtx.fill();
    //circle
    this.maskCtx.fillStyle = 'rgba(255, 255, 255, 0.0)';
    this.maskCtx.beginPath();
    this.maskCtx.arc(this.mapWidth / 2, (this.mapHeight / 2) + viewportTop, radius, 0, 2 * Math.PI, false);

    this.maskCtx.clip();
    this.maskCtx.fill();
    this.maskCtx.save();
    this.maskCtx.restore();



    //!chrome ruins the canvas lay-out on resize, so:
    //window.addEventListener('resize', redraw);
  }

}